import React, {
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  Fragment
} from 'react'
import { Select, Modal, message, Pagination, Tooltip, Button } from 'antd'

import './DocumentManagement.scss'
import { api } from '../../../../services'
import { useSelector } from 'react-redux'
import { removeMedias } from '../../../../services/api/library'
import { debounce } from 'lodash'
import Breadcrumbs from './Breadcrumbs'
import DocumentHeader from './DocumentHeader'
import SelectionBar from './SelectionBar'
import { getToken } from '../../../../services/api/user'
import FileViewer from './FileViewer'
import DocEditor from './DocEditor'
import FileDisplayView from './FileDisplayView'
import FolderModal from './FolderModal'
import MoveCopyModal from './MoveCopyModal'
import BulkActionConfirmModal from './BulkActionConfirmModal'
import BulkDeleteModal from './BulkDeleteModal'
import { MediaFilter } from '../../components'
import { UnorderedListOutlined, AppstoreOutlined } from '@ant-design/icons'
import ShareModal from './ShareModal/ShareModal'
import UploadModal from './UploadModal'
import ShortsPlayer from './ShortsPlayer'
import { general } from '../../../../constants'
import { getUserPermissions } from './utils/getPermission'
import { removeFileExtension } from './utils/removeFileExtension'
import EmptyFolder from './EmptyFolder'
import { Spinner } from '../../../../components'
import { useTranslation } from 'react-i18next'

const { Option } = Select

const DOCUMENT_TYPES = [
  { value: 'general_document', label: 'General Document' },
  { value: 'work_instructions', label: 'Work Instructions' }
]

const formatFileSize = bytes => {
  if (!bytes) return '0'
  if (bytes < 1024) return `${bytes} Bytes`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const DocumentManagement = forwardRef(
  ({ defaultQuery = {}, tab, orgUserList }, ref) => {
    // Helper function to refresh both folders and files

    const isDownloadCenter = tab === general.DOWNLOADS_CENTER

    const refreshAllData = () => {
      getAllFiles()

      if (!isDownloadCenter) {
        getAllFolders()
      }
    }

    // Helper function to refresh only folders
    const refreshFoldersOnly = (page = null, limit = null) => {
      setIsFetchingFolders(true)
      getAllFolders(query.parent_id, page, limit)
    }

    // Helper function to refresh only files
    const refreshFilesOnly = (page = null, limit = null) => {
      setIsFetchingFiles(true)
      getAllFiles(query.parent_id, page, limit)
    }

    // Expose refetch method to parent
    useImperativeHandle(ref, () => ({
      refetch: () => {
        refreshAllData()
      }
    }))
    // State for queries and pagination
    const [query, setQuery] = useState({
      search: '',
      sort: 'NEWEST',
      type: '',
      ...(isDownloadCenter && { category: '' }),
      parent_id: null,
      ...defaultQuery
    })

    const [pagination, setPagination] = useState({
      page: 1,
      limit: 15,
      totalItems: 0
    })

    // Separate folder pagination state
    const [folderPagination, setFolderPagination] = useState({
      page: 1,
      limit: 10,
      totalItems: 0
    })

    const [isDocTypeModalVisible, setIsDocTypeModalVisible] = useState(false)
    const INITIAL_DOCUMENT_DATA = {
      type: null,
      name: '',
      isCreate: false
    }

    const [documentData, setDocumentData] = useState(INITIAL_DOCUMENT_DATA)

    // Separate state for folders
    const [foldersByParentId, setFoldersByParentId] = useState({})
    const [filesByParentId, setFilesByParentId] = useState({})
    const [isSOPModalVisible, setIsSOPModalVisible] = useState(false)
    // Other state variables
    const [viewMode, setViewMode] = useState('list')
    const [isFetchingFolders, setIsFetchingFolders] = useState(false)
    const [isFetchingFiles, setIsFetchingFiles] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [isCreatingFolder, setIsCreatingFolder] = useState(false)
    const [selectedItems, setSelectedItems] = useState([])
    const [
      isCreateFolderModalVisible,
      setIsCreateFolderModalVisible
    ] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [shareModal, setShareModal] = useState({ visible: false, item: null })
    const [editingItem, setEditingItem] = useState(null)
    const [editName, setEditName] = useState('')

    const [viewingFiles, setViewingFiles] = useState([])
    const [isFileViewerVisible, setIsFileViewerVisible] = useState(false)
    const [initialFileIndex, setInitialFileIndex] = useState(0)
    const [docEditorFile, setDocEditorFile] = useState(null)
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [shlToken, setShlToken] = useState(null)
    const [moveCopyModal, setMoveCopyModal] = useState({
      visible: false,
      mode: 'move',
      item: null
    })

    const [videoShorts, setVideoShorts] = useState({
      visible: false,
      item: null
    })

    const [navigationTrigger, setNavigationTrigger] = useState(0) // Add navigation trigger

    const [bulkModal, setBulkModal] = useState({
      visible: false,
      action: null, // 'public' | 'org' | 'private'
      items: []
    })
    const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false)
    const [aiProcessingItems, setAiProcessingItems] = useState(new Set())
    const user = useSelector(state => state.user)

    const { uploadSuccess } = useSelector(state => state.library)
    const { t } = useTranslation()

    const handleDocTypeModalVisible = visible => {
      if (typeof visible === 'boolean') {
        setIsDocTypeModalVisible(visible)
      } else {
        setIsDocTypeModalVisible(prevState => !prevState)
      }
    }

    const handleDocTypeModalCancel = () => {
      setIsDocTypeModalVisible(false)
      // Reset to initial state instead of null
      setDocumentData(INITIAL_DOCUMENT_DATA)
    }

    const handleDocumentTypeCreate = () => {
      if (!documentData?.type) {
        message.warning('Please select a document type')
        return
      }
      if (!documentData?.name?.trim()) {
        message.warning('Please enter a document name')
        return
      }
      setIsDocTypeModalVisible(false)
      // Mark as created
      setDocumentData(prev => ({
        ...prev,
        isCreate: true
      }))
    }

    // Fetch data - simplified to always fetch fresh data
    const getAllFiles = async (
      parentId = query.parent_id,
      page = null,
      limit = null
    ) => {
      setIsFetchingFiles(true)
      try {
        const currentPage = page !== null ? page : pagination.page
        const currentLimit = limit !== null ? limit : pagination.limit
        const offset = (currentPage - 1) * currentLimit
        const response = await api.library.getLibraryList({
          limit: currentLimit,
          ...(!isDownloadCenter && { onlyMedia: true }),
          offset,
          ...query,
          parent_id: parentId,
          type: isDownloadCenter ? '' : query.type
        })

        const rows = response.data.rows || []
        const count = response.data.count || 0

        updateFileState(parentId, rows, count)
      } catch (error) {
        console.error('Error fetching files:', { error })

        // Check for specific AWS authorization errors
        if (
          error.response &&
          error.response.data &&
          error.response.data.errorType === 'AccessDenied'
        ) {
          message.error(
            'Access denied: Service configuration issue. Please contact your administrator.'
          )
          console.error('AWS Access Denied Error Details:', {
            errorType: error.response.data.errorType,
            errorMessage: error.response.data.errorMessage,
            trace: error.response.data.trace
          })
        } else if (error.response && error.response.status === 403) {
          message.error(
            'Access denied: You do not have permission to access this resource.'
          )
        } else if (error.response && error.response.status === 401) {
          message.error('Authentication failed. Please log in again.')
        } else {
          message.error('Failed to load documents. Please try again.')
        }
      } finally {
        setIsFetchingFiles(false)
      }
    }
    // Fetch data - simplified to always fetch fresh data
    const getAllFolders = async (
      parentId = query.parent_id,
      page = null,
      limit = null
    ) => {
      setIsFetchingFolders(true)
      try {
        const currentPage = page !== null ? page : folderPagination.page
        const currentLimit = limit !== null ? limit : folderPagination.limit
        const offset = (currentPage - 1) * currentLimit
        const response = await api.library.getSopList({
          limit: currentLimit,
          offset,
          onlyFolder: true,
          ...query,
          parent_id: parentId
        })

        const rows = response.data.rows || []
        const count = response.data.count || 0

        // Update folder state
        updateFolderState(parentId, rows, count)
      } catch (error) {
        console.error('Error fetching folders:', { error })

        // Check for specific AWS authorization errors
        if (
          error.response &&
          error.response.data &&
          error.response.data.errorType === 'AccessDenied'
        ) {
          message.error(
            'Access denied: Service configuration issue. Please contact your administrator.'
          )
          console.error('AWS Access Denied Error Details:', {
            errorType: error.response.data.errorType,
            errorMessage: error.response.data.errorMessage,
            trace: error.response.data.trace
          })
        } else if (error.response && error.response.status === 403) {
          message.error(
            'Access denied: You do not have permission to access this resource.'
          )
        } else if (error.response && error.response.status === 401) {
          message.error('Authentication failed. Please log in again.')
        } else {
          message.error('Failed to load folders. Please try again.')
        }
      } finally {
        setIsFetchingFolders(false)
      }
    }

    // Update state with fetched data
    const updateFolderState = (parentId, rows, count) => {
      const pathKey = getCustomPathKey(parentId)
      setFoldersByParentId(prev => ({ ...prev, [pathKey]: rows }))
      setFolderPagination(prev => ({ ...prev, totalItems: count }))
    }

    // Update state with fetched file data
    const updateFileState = (parentId, rows, count) => {
      const pathKey = getCustomPathKey(parentId)
      setFilesByParentId(prev => ({ ...prev, [pathKey]: rows }))
      setPagination(prev => ({ ...prev, totalItems: count }))
    }

    // Effects
    useEffect(() => {
      refreshAllData()
    }, [navigationTrigger])

    useEffect(() => {
      if (uploadSuccess.length > 0) {
        refreshAllData()
      }
    }, [uploadSuccess])

    // Combined loading state for overall component
    const isOverallLoading = isFetchingFolders || isFetchingFiles

    // Helper functions
    const getCustomPathKey = parentId => {
      return parentId ? `folder_${parentId}` : 'root'
    }

    const findFolderById = id => {
      if (!id) return null

      // Search in folders first
      for (const pathKey in foldersByParentId) {
        const items = foldersByParentId[pathKey] || []
        const found = items.find(
          item => item.id === id && item.media_format === 'FOLDER'
        )
        if (found) return found
      }

      // Search in files as fallback (in case folders are mixed with files)
      for (const pathKey in filesByParentId) {
        const items = filesByParentId[pathKey] || []
        const found = items.find(
          item => item.id === id && item.media_format === 'FOLDER'
        )
        if (found) return found
      }

      return null
    }

    const buildCustomPath = folderId => {
      if (!folderId) return '/'

      const pathParts = []
      let currentId = folderId

      while (currentId) {
        const folder = findFolderById(currentId)
        if (folder) {
          pathParts.unshift(
            folder.name || folder.title || `Folder_${folder.id}`
          )
          currentId = folder.parent_id
        } else {
          break
        }
      }

      return '/' + pathParts.join('/')
    }

    const debouncedSetSearchTerm = useMemo(
      () =>
        debounce(value => {
          setQuery(prev => ({ ...prev, search: value }))
          setPagination(prev => ({ ...prev, page: 1 }))
          setFolderPagination(prev => ({ ...prev, page: 1 }))
          setNavigationTrigger(prev => prev + 1)
        }, 300),
      []
    )

    // Navigation handler
    const navigateToFolder = folder => {
      if (typeof folder === 'object' && folder.media_format === 'FOLDER') {
        setQuery(prev => ({ ...prev, parent_id: folder.id }))
        setSelectedItems([])
        setPagination(prev => ({ ...prev, page: 1 }))
        setFolderPagination(prev => ({ ...prev, page: 1 }))
        setNavigationTrigger(prev => prev + 1)
      } else if (typeof folder === 'string' && folder === '/') {
        setQuery(prev => ({ ...prev, parent_id: null }))
        setSelectedItems([])
        setPagination(prev => ({ ...prev, page: 1 }))
        setFolderPagination(prev => ({ ...prev, page: 1 }))
        setNavigationTrigger(prev => prev + 1)
      }
    }

    // Folder creation
    const handleCreateFolder = async () => {
      if (!newFolderName.trim()) {
        message.error('Please enter a folder name')
        return
      }

      setIsCreatingFolder(true)
      let status = null
      if (tab && tab === general.PUBLIC_MEDIA) {
        status = 'public'
      } else if (tab && tab === general.MY_MEDIA) {
        status = 'private'
      } else if (tab && tab === general.ORGANIZATION_LIBRARY) {
        status = 'org'
      }
      try {
        const payload = {
          folder_name: newFolderName.trim(),
          is_folder: true,
          status: status,
          parent_id: query.parent_id
        }
        const response = await api.library.createFolder(payload)
        if (response.status === 200) {
          if (response.data?.errorMessage) {
            let msg = response.data?.errorMessage
            msg =
              msg === 'Error 400: Folder already exists at this path.'
                ? t('v5:duplicate_folder_name')
                : msg.split(':')[1]
            message.error(msg)
            return
          }
          refreshAllData()
          setIsCreateFolderModalVisible(false)
          setNewFolderName('')
          message.success(
            `${t('v5:folder')} "${newFolderName.trim()}" ${t(
              'v5:created_successfully'
            )}`
          )
        }
      } catch (error) {
        message.error(t('v5:failed_to_create_folder'))
      } finally {
        setIsCreatingFolder(false)
      }
    }

    const renameFile = async () => {
      if (!editName.trim()) {
        message.error(t('v5:please_enter_name'))
        return
      }
      setIsCreatingFolder(true)

      try {
        const payload = {
          title: editName
        }
        const response = await api.library.updateMedia(editingItem.id, payload)

        if (response.status === 200) {
          refreshAllData()
          message.success(
            `"${removeFileExtension(
              editingItem.title,
              editingItem.media_format
            )}" renamed to "${removeFileExtension(
              editName.trim(),
              editingItem.media_format
            )}"`
          )
          setEditingItem(null)
        }
      } catch (error) {
        message.error(t('v5:failed_to_rename_file_folder'))
      } finally {
        setIsCreatingFolder(false)
      }
    }

    // Get current directory items
    const getCurrentDirectoryItems = () => {
      const pathKey = getCustomPathKey(query.parent_id)
      const folders = foldersByParentId[pathKey] || []
      const files = filesByParentId[pathKey] || []

      // Combine folders and files, with folders appearing first
      return [...folders, ...files]
    }

    // Get current directory folders only
    const getCurrentDirectoryFolders = () => {
      const pathKey = getCustomPathKey(query.parent_id)
      return foldersByParentId[pathKey] || []
    }

    // Get current directory files only
    const getCurrentDirectoryFiles = () => {
      const pathKey = getCustomPathKey(query.parent_id)
      return filesByParentId[pathKey] || []
    }

    const handleDownload = async item => {
      setIsDownloading(true)

      if (
        (!Array.isArray(selectedItems) || selectedItems.length === 0) &&
        !item
      ) {
        message.warning('No files to download')
        setIsDownloading(false)
        return
      }

      // Single file download - open in new tab
      if (item || selectedItems.length === 1) {
        const id = item ? item.id : selectedItems[0]
        try {
          const response = await api.library.download({ id })
          const { downloadble_link } = response.data

          // Open in new tab instead of direct download
          window.open(downloadble_link, '_blank')

          message.success(t('v5:opened_download_link_in_new_tab'))
        } catch (error) {
          message.error(t('v5:failed_to_get_download_link'))
        }

        setIsDownloading(false)
        return
      }

      // Multiple file downloads - collect all links and open in one tab
      message.loading(t('v5:prepare_download'), 0)

      try {
        const downloadLinks = []
        let successCount = 0

        for (const id of selectedItems) {
          try {
            const response = await api.library.download({ id })
            const { downloadble_link } = response.data
            downloadLinks.push(downloadble_link)
            successCount++
          } catch (err) {
            console.error(`${t('v5:failed_download_link')}: ${id}`, err)
          }
        }

        message.destroy()

        if (downloadLinks.length > 0) {
          // Option 1: Open first link and show others in console/alert
          window.open(downloadLinks[0], '_blank')

          if (downloadLinks.length > 1) {
            // Show remaining links to user
            const remainingLinks = downloadLinks.slice(1)
            console.log('Additional download links:', remainingLinks)
            message.info(
              `${t('v5:open_first_download')} ${remainingLinks.length} ${t(
                'v5:additional_link'
              )}`
            )
          }

          message.success(
            `${t('v5:prepared')} ${successCount} ${t('v5:download_link')}`
          )
        } else {
          message.warning(t('v5:no_download_file'))
        }
      } catch (error) {
        message.destroy()
        message.error(t('v5:download_failed'))
      }

      setIsDownloading(false)
    }

    // Edit handler
    const handleEdit = item => {
      setEditingItem(item)
      setEditName(item.title)
    }
    // Share handler
    const handleShare = item => {
      setShareModal({ visible: true, item })
    }

    // Open Move/Copy modal
    const handleMoveCopy = (action, item) => {
      setMoveCopyModal({ visible: true, mode: action, item })
    }

    // After successful move/copy
    const handleMoveCopySuccess = () => {
      setMoveCopyModal({ ...moveCopyModal, visible: false })
      refreshAllData()
      setSelectedItems([])
    }

    // Handler for copy/move action (modal open)
    const handleAction = async (action, item) => {
      switch (action) {
        case 'view':
          handleView(item)
          break
        case 'download':
          handleDownload(item)
          break
        case 'edit':
          handleEdit(item)
          break
        case 'follow-unfollow': {
          const isFollowing = item.is_following
          const modalTitle = isFollowing ? 'Unfollow' : 'Follow'
          const modalContent = isFollowing
            ? `Are you sure you want to unfollow ${item.title}?`
            : `Are you sure you want to follow ${item.title}?`
          const modalOkText = isFollowing ? 'Unfollow' : 'Follow'
          const onOkHandler = async () => {
            try {
              if (isFollowing) {
                await api.library.deleteFollowUnfollow(item.id)
                message.success(`You have unfollowed ${item.title}`)
              } else {
                await api.library.followUnfollow(item.id)
                message.success(`You are now following ${item.title}`)
              }
              await refreshAllData()
            } catch (error) {
              message.error(
                isFollowing
                  ? `Failed to unfollow ${item.title}`
                  : `Failed to follow ${item.title}`
              )
            }
          }
          Modal.confirm({
            title: modalTitle,
            content: modalContent,
            okText: modalOkText,
            centered: true,
            onOk: onOkHandler
          })
          break
        }
        case 'delete':
          handleDelete(item)
          break
        case 'makeAi':
          const payload = {
            id: item.id,
            filename: item.filename,
            isAiEnabled: true,
            ai_language: 'en-US'
          }
          try {
            // Add item to processing set
            setAiProcessingItems(prev => new Set(prev).add(item.id))

            const response = await api.library.updateMedia(payload.id, payload)
            if (response) {
              message.success(
                'Success ! File is currently being indexed. This may take a few minutes.'
              )
              refreshAllData()
            }
          } catch (error) {
            message.error('Failed to indexed, please Try again!')
          } finally {
            // Remove item from processing set
            setAiProcessingItems(prev => {
              const newSet = new Set(prev)
              newSet.delete(item.id)
              return newSet
            })
          }
          break
        case 'copy':
        case 'move': {
          handleMoveCopy(action, item)
          break
        }
        case 'share': {
          handleShare(item)
          break
        }
        case 'make-public': {
          Modal.confirm({
            title: t('buttons:make_public'),
            content: t('v4:are_you_sure_you_want_to_make_this_item_public'),
            okText: t('buttons:make_public'),
            centered: true,
            onOk: async () => {
              try {
                await api.library.updateLibraryStatus({
                  action: 'PUBLIC',
                  library: [item.id]
                })
                await refreshAllData()
                message.success('Item made public successfully')
              } catch (error) {
                message.error('Failed to make item public')
              }
            }
          })
          break
        }
        case 'make-org': {
          Modal.confirm({
            title: t('v4:move_to_organization'),
            content: t(
              'v4:are_you_sure_you_want_to_copy_this_item_to_your_organization_library'
            ),
            okText: t('v4:move_to_organization'),
            cancelText: t('v4:cancel'),
            centered: true,
            onOk: async () => {
              try {
                await api.library.updateLibraryStatus({
                  action: 'ORG',
                  library: [item.id]
                })
                await refreshAllData()
                message.success('Item moved to organization successfully')
              } catch (error) {
                message.error('Failed to move item to organization')
              }
            }
          })
          break
        }
        case 'make-private': {
          Modal.confirm({
            title: t('buttons:make_private'),
            content: t('v4:are_you_sure_you_want_to_make_this_item_private'),
            okText: t('buttons:make_private'),
            cancelText: t('v4:cancel'),
            centered: true,
            onOk: async () => {
              try {
                await api.library.updateLibraryStatus({
                  action: 'PRIVATE',
                  library: [item.id]
                })
                await refreshAllData()
                message.success('Item made private successfully')
              } catch (error) {
                message.error('Failed to make item private')
              }
            }
          })
          break
        }
        default:
          break
      }
    }

    const handleDelete = item => {
      const mediaIds = item ? [item.id] : selectedItems
      const itemTitle = item
        ? item.title
        : `${selectedItems.length} selected items`
      Modal.confirm({
        title: `${t('v4:delete')} ${
          item.media_format === 'FOLDER' ? t('v5:folder') : t('v5:item')
        }`,
        content: `${t('v5:delete_confirmation')} "${itemTitle}"?`,
        okText: t('v4:delete'),
        okType: 'danger',
        cancelText: t('v4:cancel'),
        centered: true,
        onOk: async () => {
          try {
            // if (item && item.media_format === 'FOLDER') {
            //   await removeFolderMedias(item.id)
            // } else {
            //   await removeMedias({ mediaIds })
            // }
            await removeMedias({ mediaIds })
            await refreshAllData()
            setSelectedItems([])
            message.success(`"${itemTitle}" deleted successfully`)
          } catch (error) {
            message.error(`Failed to delete "${itemTitle}"`)
            console.error(error)
          }
        }
      })
    }

    // View handler
    const handleView = item => {
      if (item.media_format === 'VIDEO' && item.category === 'tips') {
        console.log('handleView tips', item)
        setVideoShorts({ visible: true, item: item })
        setTimeout(() => {
          console.log('Delayed after showing video shorts')
        }, 300)
        return
      }
      if (item.media_format === 'FOLDER') {
        navigateToFolder(item)
        return
      }
      // Check for document file extensions
      const docExt = /\.(odt|docx?|rtf|txt|ods|xlsx?|csv|odp|pptx?|odg|odf|fod[stp]|html?|xml|pdf)$/i
      const fileName = item.path ? item.path.split('/').pop() : ''
      if (fileName && docExt.test(fileName)) {
        setDocEditorFile(item)
        setIsFileViewerVisible(false)
        setViewingFiles([])
      } else {
        // Filter out document files from viewingFiles
        const docExt = /\.(odt|docx?|rtf|txt|ods|xlsx?|csv|odp|pptx?|odg|odf|fod[stp]|html?|xml|pdf)$/i
        const currentFiles = getCurrentDirectoryItems().filter(
          file =>
            file.media_format !== 'FOLDER' &&
            (!file.path || !docExt.test(file.path.split('/').pop()))
        )
        const fileIndex = currentFiles.findIndex(file => file.id === item.id)
        setViewingFiles(currentFiles)
        setIsFileViewerVisible(true)
        setInitialFileIndex(fileIndex >= 0 ? fileIndex : 0)
        setDocEditorFile(null)
      }
    }

    // Breadcrumb handler
    const getBreadcrumbFolders = () => {
      const crumbs = []
      if (!query.parent_id) return crumbs

      let folderId = query.parent_id
      const breadcrumbChain = []

      while (folderId) {
        const folder = findFolderById(folderId)
        if (folder) {
          breadcrumbChain.unshift(folder)
          folderId = folder.parent_id
        } else {
          break
        }
      }

      return breadcrumbChain
    }

    // Filtered data
    const filteredAndSortedData = useMemo(() => {
      return getCurrentDirectoryItems()
    }, [foldersByParentId, filesByParentId, query.parent_id])

    // Open modal for bulk action
    const openBulkModal = action => {
      const items = getCurrentDirectoryItems().filter(item =>
        selectedItems.includes(item.id)
      )
      setBulkModal({ visible: true, action, items })
    }

    // After successful bulk action
    const handleBulkActionSuccess = () => {
      setBulkModal({ ...bulkModal, visible: false })
      setSelectedItems([])
      refreshAllData()
    }

    // Bulk action handlers now only open the modal
    const handleBulkMakePublic = () => openBulkModal('public')
    const handleBulkMakeOrg = () => openBulkModal('org')
    const handleBulkMakePrivate = () => openBulkModal('private')

    // Open bulk delete modal
    const handleBulkDelete = () => {
      setBulkDeleteModalVisible(true)
    }

    // After successful delete
    const handleBulkDeleteSuccess = () => {
      setBulkDeleteModalVisible(false)
      setSelectedItems([])
      refreshAllData()
    }

    const handleOnItemClicked = id => {
      // This function is used by FileDisplayView component
    }

    const handleBulkCopy = () => {
      setMoveCopyModal({ visible: true, mode: 'copy', item: selectedItems })
    }
    const handleBulkMove = () => {
      setMoveCopyModal({ visible: true, mode: 'move', item: selectedItems })
    }

    const isSuperAdmin = user && user.info && user.info.is_super_admin
    const isAdmin = user && user.info && user.info.isAdmin

    useEffect(() => {
      const fetchToken = async () => {
        try {
          const token = await getToken()
          setShlToken(token)
        } catch (err) {
          console.error('Error fetching SHL token:', err)
        }
      }

      fetchToken()
    }, [])

    const handleSetIsSOPModalVisible = val => {
      setIsSOPModalVisible(!!val)

      // 🔥 Reset docEditorFile only when closing modal
      if (!val) {
        setDocEditorFile(null)
      }
    }

    console.log(
      'check orgid: ',
      user && user.info && user.info.active_organization_id,
      'docEditorFile',
      docEditorFile
    )

    return (
      <Fragment>
        <div className="document-management">
          {/* Document Type Selection Modal */}
          <Modal
            visible={isDocTypeModalVisible}
            onCancel={handleDocTypeModalCancel}
            footer={null}
            width="40%"
            style={{ maxWidth: '1400px', top: 20 }}
            destroyOnClose
            title="Select Document Type"
          >
            <div style={{ maxWidth: '600px', margin: '40px auto' }}>
              {/* Document Type */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  {t('v5:document_type')}{' '}
                  <span style={{ color: '#f5222d' }}>*</span>
                </label>

                <Select
                  placeholder={t('v5:select_document_type')}
                  size="large"
                  style={{ width: '100%' }}
                  value={documentData?.type}
                  onChange={value => {
                    setDocumentData(prev => {
                      // Ensure prev is never null
                      const safePrev = prev || INITIAL_DOCUMENT_DATA
                      return {
                        ...safePrev,
                        type: value,
                        isCreate: false
                      }
                    })
                  }}
                >
                  {DOCUMENT_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>

                {!documentData?.type && (
                  <div
                    style={{
                      color: '#f5222d',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}
                  >
                    {t('v5:please_select_document_type')}
                  </div>
                )}
              </div>

              {/* Document Name Input */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  {t('v5:document_name')}{' '}
                  <span style={{ color: '#f5222d' }}>*</span>
                </label>

                <input
                  type="text"
                  className="ant-input ant-input-lg"
                  placeholder="Enter document name"
                  style={{ width: '100%' }}
                  value={documentData?.name || ''}
                  onChange={e => {
                    const newName = e.target.value
                    setDocumentData(prev => {
                      // Ensure prev is never null
                      const safePrev = prev || INITIAL_DOCUMENT_DATA
                      return {
                        ...safePrev,
                        name: newName,
                        isCreate: false
                      }
                    })
                  }}
                />

                {documentData && !documentData.name?.trim() && (
                  <div
                    style={{
                      color: '#f5222d',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}
                  >
                    {t('v5:please_enter_document_name')}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div style={{ marginTop: '40px', textAlign: 'right' }}>
                <Button
                  onClick={handleDocTypeModalCancel}
                  style={{ marginRight: '12px' }}
                  size="large"
                >
                  {t('v4:cancel')}
                </Button>

                <Button
                  type="primary"
                  size="large"
                  disabled={!documentData?.type || !documentData?.name?.trim()}
                  onClick={handleDocumentTypeCreate}
                >
                  {t('v2:create')}
                </Button>
              </div>
            </div>
          </Modal>
          <DocumentHeader
            orgUserList={orgUserList}
            docEditorFile={docEditorFile}
            userId={user && user.info && user.info.id}
            userName={(user && user.info && user.info.username) || ''}
            userOrg={user.info.active_organization_id}
            tab={tab}
            defaultQuery={defaultQuery}
            isSuperAdmin={isSuperAdmin}
            isAdmin={isAdmin}
            query={query}
            handleChangeSearch={e => debouncedSetSearchTerm(e.target.value)}
            openUploadModal={() => setShowUploadModal(true)}
            setIsCreateFolderModalVisible={setIsCreateFolderModalVisible}
            user={user}
            shlToken={shlToken}
            setIsSOPModalVisible={handleSetIsSOPModalVisible}
            documentData={documentData}
            handleDocTypeModalVisible={handleDocTypeModalVisible}
            isSOPModalVisible={isSOPModalVisible}
            resetDocumentData={handleDocTypeModalCancel}
          />
          <div className="breadcrumb-container">
            <MediaFilter
              isDownloadCenter={isDownloadCenter}
              ctrlHandleFilterChange={type => {
                console.log('type console', type)
                if (type === 'All') {
                  type = ''
                }
                setSelectedItems([])

                if (!isDownloadCenter) {
                  setQuery(prev => ({
                    ...prev,
                    ...(type !== 'SHORTS' && { type }),
                    isTip: type === 'SHORTS'
                  }))
                } else {
                  setQuery(prev => ({
                    ...prev,
                    category:
                      type === 'maze,skill' || type === 'All' ? '' : type
                  }))
                }

                setPagination(prev => ({ ...prev, page: 1 }))
                setFolderPagination(prev => ({ ...prev, page: 1 }))
                setNavigationTrigger(prev => prev + 1)
              }}
            />

            <div className="action-buttons">
              <div className="view-toggle">
                <div className={`toggle-slider ${viewMode}`} />
                <Tooltip title={t('v5:list_view')} placement="bottom">
                  <div
                    className={`toggle-icon ${
                      viewMode === 'list' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('list')}
                  >
                    <UnorderedListOutlined />
                  </div>
                </Tooltip>
                <Tooltip title={t('v5:grid_view')} placement="bottom">
                  <div
                    className={`toggle-icon ${
                      viewMode === 'grid' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('grid')}
                  >
                    <AppstoreOutlined />
                  </div>
                </Tooltip>
              </div>
              <div className="sort-dropdowns">
                <label
                  htmlFor="sort-select"
                  style={{ fontWeight: 500, marginRight: 8 }}
                >
                  {t('labels:sort_by')}
                </label>
                <Select
                  id="sort-select"
                  value={(() => {
                    switch (query.sort) {
                      case 'ASC':
                        return 'Name (A-Z)'
                      case 'DESC':
                        return 'Name (Z-A)'
                      case 'NEWEST':
                        return `${t('v4:date_modified')} (↑)`
                      case 'OLDEST':
                        return `${t('v4:date_modified')} (↓)`
                      default:
                        return query.sort
                    }
                  })()}
                  size="large"
                  onChange={sort => {
                    let newSort = sort
                    if (sort === 'Name (A-Z)') newSort = 'ASC'
                    else if (sort === 'Name (Z-A)') newSort = 'DESC'
                    else if (sort === `${t('v4:date_modified')} (↑)`)
                      newSort = 'NEWEST'
                    else if (sort === `${t('v4:date_modified')} (↓)`)
                      newSort = 'OLDEST'
                    setQuery(prev => ({ ...prev, sort: newSort }))
                    setPagination(prev => ({ ...prev, page: 1 }))
                    setFolderPagination(prev => ({ ...prev, page: 1 }))
                    setNavigationTrigger(prev => prev + 1)
                  }}
                  style={{ minWidth: 160, marginRight: 8 }}
                  dropdownStyle={{ minWidth: 180 }}
                >
                  {query.sort !== 'ASC' && (
                    <Option value="Name (A-Z)">{t('labels:name')} (A-Z)</Option>
                  )}
                  {query.sort !== 'DESC' &&
                    query.sort !== 'NEWEST' &&
                    query.sort !== 'OLDEST' && (
                      <Option value="Name (Z-A)">
                        {t('labels:name')} (Z-A)
                      </Option>
                    )}
                  {query.sort !== 'NEWEST' && (
                    <Option value="Date Modified (↑)">
                      {t('v4:date_modified')} (↑)
                    </Option>
                  )}
                  {query.sort !== 'OLDEST' &&
                    query.sort !== 'ASC' &&
                    query.sort !== 'DESC' && (
                      <Option value="Date Modified (↓)">
                        {t('v4:date_modified')} (↓)
                      </Option>
                    )}
                </Select>
              </div>
            </div>
          </div>
          <div className="breadcrumb-container">
            <div className="breadcrumb-content">
              <Breadcrumbs
                getBreadcrumbFolders={getBreadcrumbFolders}
                navigateToFolder={navigateToFolder}
                currentPath={buildCustomPath(query.parent_id)}
                query={query}
              />
            </div>
          </div>

          <div className="document-content">
            {isOverallLoading &&
            getCurrentDirectoryFolders().length === 0 &&
            getCurrentDirectoryFiles().length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spinner />
              </div>
            ) : getCurrentDirectoryFolders().length === 0 &&
              getCurrentDirectoryFiles().length === 0 &&
              !isOverallLoading ? (
              <EmptyFolder />
            ) : (
              <>
                {(getCurrentDirectoryFolders().length > 0 ||
                  isFetchingFolders) && (
                  <div className="folders-section">
                    <FileDisplayView
                      isFolder={true}
                      user={user}
                      orgUserList={orgUserList}
                      searchType={query.type}
                      data={getCurrentDirectoryFolders()}
                      onItemClick={handleView}
                      selectedItems={selectedItems}
                      onSelectionChange={setSelectedItems}
                      onAction={handleAction}
                      formatFileSize={formatFileSize}
                      loading={isFetchingFolders}
                      setIsCreateFolderModalVisible={
                        setIsCreateFolderModalVisible
                      }
                      viewType={viewMode}
                      defaultQuery={defaultQuery}
                      setOnItemClicked={handleOnItemClicked}
                      refreshData={refreshFoldersOnly}
                      aiProcessingItems={aiProcessingItems}
                      isDownloadCenter={isDownloadCenter}
                    />
                    {/* Folder Pagination */}
                    {!isFetchingFolders && folderPagination.totalItems > 0 && (
                      <Pagination
                        current={folderPagination.page}
                        pageSize={folderPagination.limit}
                        total={folderPagination.totalItems}
                        onChange={(page, pageSize) => {
                          setSelectedItems([])
                          setFolderPagination(prev => ({
                            ...prev,
                            page,
                            limit: pageSize
                          }))
                          refreshFoldersOnly(page, pageSize)
                        }}
                        style={{
                          marginTop: 16,
                          textAlign: 'right',
                          background: 'transparent'
                        }}
                        className="custom-pagination"
                        itemRender={(page, type, originalElement) => {
                          if (type === 'prev' || type === 'next') {
                            return React.cloneElement(originalElement, {
                              style: {
                                border: 'none',
                                boxShadow: 'none',
                                background: 'transparent'
                              }
                            })
                          }
                          return React.cloneElement(originalElement, {
                            style: {
                              borderRadius: '50%!important'
                            }
                          })
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Files Section */}
                {(getCurrentDirectoryFiles().length > 0 || isFetchingFiles) && (
                  <div className="files-section">
                    <FileDisplayView
                      isFolder={false}
                      user={user}
                      orgUserList={orgUserList}
                      searchType={query.type}
                      data={getCurrentDirectoryFiles()}
                      onItemClick={handleView}
                      selectedItems={selectedItems}
                      onSelectionChange={setSelectedItems}
                      onAction={handleAction}
                      formatFileSize={formatFileSize}
                      loading={isFetchingFiles}
                      setIsCreateFolderModalVisible={
                        setIsCreateFolderModalVisible
                      }
                      viewType={viewMode}
                      defaultQuery={defaultQuery}
                      setOnItemClicked={handleOnItemClicked}
                      refreshData={refreshFilesOnly}
                      aiProcessingItems={aiProcessingItems}
                      isDownloadCenter={isDownloadCenter}
                    />
                    {/* File Pagination */}
                    {!isFetchingFiles && (
                      <Pagination
                        current={pagination.page}
                        pageSize={pagination.limit}
                        total={pagination.totalItems}
                        onChange={(page, pageSize) => {
                          setSelectedItems([])
                          setPagination(prev => ({
                            ...prev,
                            page,
                            limit: pageSize
                          }))
                          refreshFilesOnly(page, pageSize)
                        }}
                        style={{
                          marginTop: 16,
                          textAlign: 'right',
                          background: 'transparent'
                        }}
                        className="custom-pagination"
                        itemRender={(page, type, originalElement) => {
                          if (type === 'prev' || type === 'next') {
                            return React.cloneElement(originalElement, {
                              style: {
                                border: 'none',
                                boxShadow: 'none',
                                background: 'transparent'
                              }
                            })
                          }
                          return React.cloneElement(originalElement, {
                            style: {
                              borderRadius: '50%!important'
                            }
                          })
                        }}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {selectedItems.length > 0 && (
            <SelectionBar
              isDownloading={isDownloading}
              defaultQuery={defaultQuery}
              selectedItems={selectedItems}
              handleDownload={handleDownload}
              handleDelete={handleBulkDelete}
              // Bulk actions based on context
              handleMakePublic={
                !(defaultQuery.isPublic || defaultQuery.isOrg)
                  ? handleBulkMakePublic
                  : undefined
              }
              handleMakeOrg={
                !(defaultQuery.isPublic || defaultQuery.isOrg)
                  ? handleBulkMakeOrg
                  : undefined
              }
              handleMakePrivate={
                defaultQuery.isPublic || defaultQuery.isOrg
                  ? handleBulkMakePrivate
                  : undefined
              }
              handleBulkCopy={handleBulkCopy}
              handleBulkMove={handleBulkMove}
            />
          )}

          {/* Modals remain the same as before */}
          <FolderModal
            visible={isCreateFolderModalVisible}
            mode="create"
            loading={isCreatingFolder}
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onOk={handleCreateFolder}
            onCancel={() => setIsCreateFolderModalVisible(false)}
            autoFocus
          />

          <FolderModal
            visible={!!editingItem}
            mode="rename"
            loading={isCreatingFolder}
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onOk={renameFile}
            onCancel={() => {
              setEditingItem(null)
              setEditName('')
            }}
            autoFocus
            mediaFormat={editingItem ? editingItem.media_format : undefined}
          />

          <UploadModal
            user={user}
            isLibrary={true}
            parent_id={query.parent_id}
            visible={showUploadModal}
            onCancel={() => {
              setShowUploadModal(false)
            }}
            tab={tab}
            refreshData={refreshAllData}
            documentTypes={DOCUMENT_TYPES}
            shlToken={shlToken}
          />
          <ShareModal
            visible={shareModal.visible}
            item={shareModal.item}
            onCancel={() => {
              setShareModal({ visible: false, item: null })
            }}
          />

          {/* Viewer/Editor logic */}
          {docEditorFile && orgUserList.length > 0 ? (
            // <DocEditor
            //   orgUserList={orgUserList}
            //   isDocOpened={!!docEditorFile}
            //   documentName={docEditorFile.title}
            //   documentId={docEditorFile.path}
            //   userId={docEditorFile.creator.id}
            //   userName={docEditorFile.creator.username}
            //   closeModal={() => setDocEditorFile(null)}
            //   shlToken={shlToken}
            //   {...getUserPermissions(user, orgUserList, docEditorFile, true)}
            // />
            <></>
          ) : docEditorFile ? (
            <div>{t('v5:loading_organization')}</div>
          ) : (
            <FileViewer
              visible={isFileViewerVisible}
              files={viewingFiles}
              initialIndex={initialFileIndex}
              onClose={() => {
                setIsFileViewerVisible(false)
                setViewingFiles([])
              }}
              onDownload={handleDownload}
            />
          )}

          <MoveCopyModal
            user={user}
            defaultQuery={defaultQuery}
            parent_id={query.parent_id}
            visible={moveCopyModal.visible}
            mode={moveCopyModal.mode}
            item={moveCopyModal.item}
            onSuccess={handleMoveCopySuccess}
            onCancel={() =>
              setMoveCopyModal({ ...moveCopyModal, visible: false })
            }
          />

          {/* Bulk Action Confirm Modal */}
          <BulkActionConfirmModal
            visible={bulkModal.visible}
            items={bulkModal.items}
            actionLabel={
              bulkModal.action === 'public'
                ? t('buttons:make_public')
                : bulkModal.action === 'org'
                ? t('buttons:make_org')
                : t('buttons:make_private')
            }
            apiAction={
              bulkModal.action === 'public'
                ? 'PUBLIC'
                : bulkModal.action === 'org'
                ? 'ORG'
                : 'PRIVATE'
            }
            onSuccess={handleBulkActionSuccess}
            onCancel={() => setBulkModal({ ...bulkModal, visible: false })}
            requireTerms={bulkModal.action === 'public'}
            disclaimer={
              bulkModal.action === 'public'
                ? t('v5:by_making_items_public')
                : ''
            }
            message={
              bulkModal.action === 'public'
                ? t('v5:the_selected_items_will_be_copied')
                : bulkModal.action === 'org'
                ? t('v5:the_selected_items_will_be_copied_to_org')
                : t('v5:the_selected_items_will_be_copied_to_your_private')
            }
          />

          {videoShorts && videoShorts.visible && (
            <ShortsPlayer
              defaultQuery={defaultQuery}
              isAdmin={isAdmin}
              visible={videoShorts.visible}
              item={videoShorts.item}
              shlToken={shlToken}
              onCancelTips={() =>
                setVideoShorts({ visible: false, item: null })
              }
              hideButton={false}
              userData={user && user.info}
            />
          )}

          {/* Bulk Delete Modal */}
          <BulkDeleteModal
            visible={bulkDeleteModalVisible}
            items={getCurrentDirectoryItems().filter(item =>
              selectedItems.includes(item.id)
            )}
            onSuccess={handleBulkDeleteSuccess}
            onCancel={() => setBulkDeleteModalVisible(false)}
          />
        </div>
      </Fragment>
    )
  }
)

export default DocumentManagement
